import { IonText } from '@ionic/react';
import { orderBy } from 'lodash';
import { CSSProperties, ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteProps } from 'react-router';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';
import {
  File,
  Machine,
  useListMachineFilesQuery,
} from '../../api/graphql/generated';
import { LoadingRetry } from '../graphql/LoadingRetry';
import { MachineFileItem } from './machine-file-item';

interface MachineFilesProps extends RouteProps {
  machine?: Machine;
  filesPath: string | null;
}

const MachineFiles = (props: MachineFilesProps): ReactElement | null => {
  const { machine, filesPath } = props;

  const { i18n } = useTranslation();

  const [orderedFiles, setOrderedFiles] = useState<File[]>([]);
  const { data, loading, startPolling, stopPolling, error, refetch } =
    useListMachineFilesQuery({
      variables: { input: { id: machine?.id ?? '', path: filesPath } },
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    });

  useEffect(() => {
    if (data?.listMachineFiles.items && !loading) {
      setOrderedFiles(
        orderBy(data?.listMachineFiles.items, ['type', 'name'], ['asc', 'asc'])
      );

      if (startPolling) {
        startPolling(5000);
      }

      return () => {
        if (stopPolling) {
          stopPolling();
        }
      };
    }
  }, [data, loading, startPolling, stopPolling]);

  const noResults = (
    <div className="ion-padding ion-text-center">
      <IonText color="medium">{i18n.t('common:no_results_found')}</IonText>
    </div>
  );

  return (
    <LoadingRetry
      loading={loading}
      error={error}
      refetch={refetch}
      results={{
        hasResults: orderedFiles.length > 0,
        noResultsComponent: noResults,
      }}
    >
      <div style={{ height: '100%' }}>
        <AutoSizer>
          {({ height, width }) => {
            return (
              <FixedSizeList
                height={height}
                width={width}
                itemCount={orderedFiles.length || 0}
                itemData={orderedFiles}
                itemSize={61}
              >
                {MachineFileRow}
              </FixedSizeList>
            );
          }}
        </AutoSizer>
      </div>
    </LoadingRetry>
  );
};

interface MachineFileRowProps {
  data: File[];
  index: number;
  style: CSSProperties;
}

const MachineFileRow = (props: MachineFileRowProps) => {
  const file = props.data[props.index];
  return (
    <MachineFileItem
      key={file.id}
      item={file}
      ionItemProps={{ style: props.style }}
    />
  );
};

export { MachineFiles };
